import { Link } from '@tanstack/react-router';
import {
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  type PaginationState,
  useReactTable,
} from '@tanstack/react-table';
import { format } from 'date-fns';
import { Loader2Icon } from 'lucide-react';
import { useMemo, useState, useEffect } from 'react';

import {
  Label,
  useGetAccountsReceivableStudentGrouping,
} from '~entities/dashboard/api/get-accounts-receivable-student-grouping';
import { getStoredPageSize } from '~shared/helpers/page-size-preference';
import { formatDateOnlySafe } from '~shared/helpers/datetime';
import {
  isArInvoicePastDue,
  pickArInvoiceEntityTitle,
} from '~shared/utils/ar-invoice-display';

import { PaymentStatus } from '~entities/payment/constants';

import { DataTablePagination } from '~shared/components/table/data-table-pagination';
import { Avatar, AvatarFallback } from '~shared/shadcn/ui/avatar';
import { Badge } from '~shared/shadcn/ui/badge';
import { cn } from '~shared/shadcn/lib/utils';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~shared/shadcn/ui/select';

interface BalanceDueTabProps {
  sessionId: string;
  selectedRange?: Label;
  onRangeChange?: (range: Label) => void;
  hideRangeSelectOnMobile?: boolean;
}

type AccountsReceivableStudentGrouping = NonNullable<
  ReturnType<typeof useGetAccountsReceivableStudentGrouping>['data']
>;
type AccountsReceivableInvoice = AccountsReceivableStudentGrouping[number]['invoices'][number];

const columnHelper = createColumnHelper<AccountsReceivableInvoice>();
const paginationColumns = [columnHelper.display({ id: 'cards' })];

interface BalanceDueRangeSelectProps {
  value: Label;
  onChange: (range: Label) => void;
  className?: string;
}

export const BalanceDueRangeSelect = ({
  value,
  onChange,
  className,
}: BalanceDueRangeSelectProps) => (
  <Select
    value={value}
    onValueChange={(nextValue) => {
      onChange(nextValue as Label);
    }}
  >
    <SelectTrigger className={cn('w-[180px]', className)}>
      <SelectValue placeholder='Select a range' />
    </SelectTrigger>
    <SelectContent>
      <SelectGroup>
        <SelectItem value={Label.Days0To30}>0-30 days</SelectItem>
        <SelectItem value={Label.Days31To60}>31-60 days</SelectItem>
        <SelectItem value={Label.Days61To90}>61-90 days</SelectItem>
        <SelectItem value={Label.Days90Plus}>90+ days</SelectItem>
      </SelectGroup>
    </SelectContent>
  </Select>
);

export const BalanceDueTab = ({
  sessionId,
  selectedRange: selectedRangeProp,
  onRangeChange,
  hideRangeSelectOnMobile = false,
}: BalanceDueTabProps) => {
  const [internalRange, setInternalRange] = useState<Label>(Label.Days0To30);
  const selectedRange = selectedRangeProp ?? internalRange;
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: getStoredPageSize(),
  });

  const handleRangeChange = (value: Label) => {
    if (onRangeChange) {
      onRangeChange(value);
    } else {
      setInternalRange(value);
    }
  };

  useEffect(() => {
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, [selectedRange]);

  const {
    data: accountsReceivableStudentGrouping,
    isFetching: isFetchingAccountsReceivableStudentGrouping,
  } = useGetAccountsReceivableStudentGrouping({
    sessionId,
    range: selectedRange,
  });

  const invoices = useMemo(
    () =>
      (accountsReceivableStudentGrouping ?? []).flatMap((item) =>
        item.invoices.filter(
          (invoice) =>
            (invoice.status === PaymentStatus.PENDING ||
              invoice.status === PaymentStatus.FAILED ||
              invoice.status === PaymentStatus.PARTIALLY_PAID) &&
            isArInvoicePastDue(invoice.dueDate, invoice.createdAt),
        ),
      ),
    [accountsReceivableStudentGrouping],
  );

  const table = useReactTable({
    data: invoices,
    columns: paginationColumns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange: setPagination,
    state: {
      pagination,
    },
  });

  return (
    <div className='w-full min-w-0 max-w-full space-y-4'>
      <div
        className={cn(
          'flex items-center justify-end',
          hideRangeSelectOnMobile && 'hidden md:flex',
        )}
      >
        <BalanceDueRangeSelect
          value={selectedRange}
          onChange={handleRangeChange}
        />
      </div>
      <div className='w-full min-w-0 max-w-full'>
        {isFetchingAccountsReceivableStudentGrouping ? (
          <div className='flex items-center justify-center'>
            <Loader2Icon className='size-8 animate-spin' />
          </div>
        ) : (
          <div className='w-full min-w-0 max-w-full'>
            {invoices.length > 0 ? (
              <div className='w-full min-w-0 max-w-full space-y-4'>
                <div className='w-full min-w-0 max-w-full space-y-2'>
                  {table.getRowModel().rows.map((row) => {
                    const invoice = row.original;

                    return (
                      <div
                        key={invoice._id}
                        className='flex w-full min-w-0 flex-col gap-3 rounded-xl border border-border/50 p-3.5 transition-all duration-150 hover:-translate-y-px hover:bg-muted/50 hover:shadow-md md:flex-row md:items-center md:justify-between'
                      >
                        <div className='flex min-w-0 shrink-0 items-center gap-2 md:max-w-[40%]'>
                          <Avatar className='shrink-0'>
                            <AvatarFallback className='bg-primary font-semibold text-primary-foreground'>
                              {invoice.studentName
                                .split(' ')
                                .map((name) => name.charAt(0))
                                .join('')
                                .toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                          <Link
                            to='/students/$studentId/details'
                            params={{ studentId: invoice.studentId }}
                            className='min-w-0 truncate text-sm font-semibold text-secondary hover:underline'
                          >
                            {invoice.studentName}
                          </Link>
                        </div>
                        <div className='flex min-w-0 flex-wrap items-center gap-2 md:flex-1 md:justify-end'>
                          <Badge
                            variant='outline'
                            className='h-auto max-w-full items-center gap-1 whitespace-normal break-words border-blue-200 bg-blue-50 text-xs font-medium text-blue-700'
                          >
                            {pickArInvoiceEntityTitle(
                              invoice.eventTitle,
                              invoice.enrollmentTitle,
                            ) || '—'}
                          </Badge>
                          <Badge
                            variant='outline'
                            className='items-center gap-1 border-red-300 bg-red-100 text-red-700'
                          >
                            Amount:{' '}
                            {(invoice.outstandingAmount ?? invoice.finalAmount ?? 0).toFixed(2)}
                          </Badge>
                          <Badge
                            variant='outline'
                            className='items-center gap-1 border-yellow-300 bg-yellow-100 text-yellow-700'
                          >
                            Attempts: {invoice.metadata.attemptCount}
                          </Badge>
                          <Badge
                            variant='outline'
                            className='items-center gap-1 border-orange-300 bg-orange-100 text-orange-700'
                          >
                            Due Date:{' '}
                            {invoice.dueDate
                              ? formatDateOnlySafe(invoice.dueDate, 'MMM dd, yyyy')
                              : format(new Date(invoice.createdAt), 'MMM dd, yyyy')}
                          </Badge>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <DataTablePagination table={table} />
              </div>
            ) : (
              <div className='text-center text-sm text-muted-foreground'>
                No students found
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
